import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { refreshTenantAccessToken } from '@/lib/tenant-token-refresh';

const REFRESH_BEFORE_EXPIRY_MS = 2 * 60 * 1000; // 2 minutes

function parseJwtExp(token: string): number | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const json = atob(segment.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    return typeof payload['exp'] === 'number' ? payload['exp'] : null;
  } catch {
    return null;
  }
}

/**
 * Schedules a silent token refresh 2 minutes before the tenant access token expires.
 * Arms a new timer whenever accessToken changes (i.e. after each rotation).
 * Calls the same singleton used by the 401 interceptor — concurrent attempts share one request.
 */
export function useTenantAutoRefresh(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const exp = parseJwtExp(accessToken);
    if (exp === null) return;

    const msUntilExpiry = exp * 1000 - Date.now();
    const msUntilRefresh = msUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;

    if (msUntilRefresh <= 0) {
      void refreshTenantAccessToken().catch(() => {
        // clearAuth() already called inside refreshTenantAccessToken on failure
      });
      return;
    }

    const timer = setTimeout(() => {
      void refreshTenantAccessToken().catch(() => {
        // clearAuth() already called inside refreshTenantAccessToken on failure
      });
    }, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [accessToken, isAuthenticated]);
}
